import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Box, Typography, Stack, Button, Paper, IconButton, TextField, Alert } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { api } from '../api/client';

interface Recipient { id: number; email: string; fullName?: string; enabled: boolean; createdAt: string; }

export function AlertRecipientsPage() {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['alert-recipients'],
    queryFn: () => api.get<Recipient[]>('/alert-recipients').then((r) => r.data),
  });

  const add = useMutation({
    mutationFn: () => api.post('/alert-recipients', { email, fullName: fullName || undefined }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['alert-recipients'] }); setEmail(''); setFullName(''); setErr(null); },
    onError: (e: unknown) => {
      const ax = e as { response?: { data?: { message?: string } } };
      setErr(ax.response?.data?.message ?? 'Error');
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/alert-recipients/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alert-recipients'] }),
  });

  const cols: GridColDef<Recipient>[] = [
    { field: 'email', headerName: 'Email', flex: 1.5 },
    { field: 'fullName', headerName: 'Nombre', flex: 1 },
    { field: 'enabled', headerName: 'Activo', flex: 0.5, renderCell: (p) => (p.value ? '✓' : '—') },
    { field: 'actions', headerName: '', flex: 0.4, sortable: false, renderCell: (p) => (
      <IconButton size="small" onClick={() => remove.mutate(p.row.id)}><DeleteIcon fontSize="small" /></IconButton>
    )},
  ];

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>Destinatarios de alerta</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Reciben email cuando una placa no se puede leer o no está en catálogo.
      </Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>{err}</Alert>}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField label="Email" value={email} onChange={(e) => setEmail(e.target.value)} fullWidth />
          <TextField label="Nombre (opcional)" value={fullName} onChange={(e) => setFullName(e.target.value)} fullWidth />
          <Button variant="contained" onClick={() => add.mutate()} disabled={!email}>Agregar</Button>
        </Stack>
      </Paper>
      <Paper sx={{ height: 480 }}>
        <DataGrid<Recipient>
          rows={list.data ?? []}
          columns={cols}
          getRowId={(r) => r.id}
          disableRowSelectionOnClick
        />
      </Paper>
    </Box>
  );
}
